
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { useEffect, useState } from 'react'

interface SleepDurationData {
  duration: string;
  count: number;
}

export interface SleepDurationHistogramProps {
  data?: SleepDurationData[]
  childId?: string;
  records?: any[];
}

export function SleepDurationHistogram({ data, childId, records }: SleepDurationHistogramProps) {
  const [chartData, setChartData] = useState<SleepDurationData[]>([]);

  useEffect(() => {
    if (data) {
      setChartData(data);
      return;
    }
    
    if (records?.length) {
      // Process records to create histogram data
      const durationCounts: Record<string, number> = {
        "0-2h": 0,
        "2-4h": 0,
        "4-6h": 0,
        "6-8h": 0,
        "8-10h": 0,
        "10-12h": 0,
        "12h+": 0
      };
      
      records.forEach(record => {
        const startTime = new Date(record.start_time);
        const endTime = new Date(record.end_time);
        const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
        
        if (durationHours < 2) durationCounts["0-2h"]++;
        else if (durationHours < 4) durationCounts["2-4h"]++;
        else if (durationHours < 6) durationCounts["4-6h"]++;
        else if (durationHours < 8) durationCounts["6-8h"]++;
        else if (durationHours < 10) durationCounts["8-10h"]++;
        else if (durationHours < 12) durationCounts["10-12h"]++;
        else durationCounts["12h+"]++;
      });
      
      // Convert to chart data format
      const formattedData = Object.entries(durationCounts).map(([duration, count]) => ({
        duration,
        count
      }));
      
      setChartData(formattedData);
    } else {
      setChartData([]);
    }
  }, [data, records]);

  if (!chartData?.length) {
    return <div className="text-gray-400">No sleep duration data available</div>
  }

  return (
    <div className="w-full h-[300px]">
      <h3 className="text-sm font-medium mb-4">Sleep Duration Distribution</h3>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
          <XAxis 
            dataKey="duration" 
            className="text-xs"
            tick={{ fill: 'currentColor' }}
          />
          <YAxis className="text-xs" tick={{ fill: 'currentColor' }} />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
          />
          <Bar 
            dataKey="count" 
            name="Sleep Sessions" 
            fill="#8b5cf6" 
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
